import {
  BarcodeFormat,
  BarcodeScanner,
  type PermissionStatus,
} from '@capacitor-mlkit/barcode-scanning';
import type { PluginListenerHandle } from '@capacitor/core';
import type { ScanBackend } from './scanner';

/**
 * Native backend: `startScan` of @capacitor-mlkit/barcode-scanning, which runs
 * CameraX with the *bundled* ML Kit model and needs no Google Play Services
 * (unlike `scan`, which delegates to the GMS code scanner and is never used).
 * The camera preview sits behind the transparent WebView.
 */
export const startNativeScan: ScanBackend = async ({ onText, onError }) => {
  let status: PermissionStatus = await BarcodeScanner.checkPermissions();
  if (status.camera !== 'granted') status = await BarcodeScanner.requestPermissions();
  if (status.camera !== 'granted') {
    onError('permission-denied');
    return () => undefined;
  }

  const handles: PluginListenerHandle[] = [];
  handles.push(
    await BarcodeScanner.addListener('barcodesScanned', ({ barcodes }) => {
      const text = barcodes.find((b) => b.rawValue)?.rawValue;
      if (text) onText(text);
    }),
    await BarcodeScanner.addListener('scanError', () => onError('unavailable')),
  );
  try {
    await BarcodeScanner.startScan({ formats: [BarcodeFormat.QrCode] });
  } catch {
    await Promise.all(handles.map((h) => h.remove()));
    onError('unavailable');
    return () => undefined;
  }

  return async () => {
    await Promise.all(handles.map((h) => h.remove()));
    try {
      await BarcodeScanner.stopScan();
    } catch {
      // The scanner may already be stopped.
    }
  };
};
